import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { verifyCode, resendCode } from "./auth";
import ResetForm from "./ResetForm";
import {
  codeToDigits,
  normalizeVerificationCode,
} from "../../utils/loginVerifyUtils";
import { clearDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";

const CODE_LENGTH = 6;

function VerifyCode({ email, initialCode = "", setIsResetPass }) {
  const { t } = useTranslation("login");
  const [codeDigits, setCodeDigits] = useState(() => codeToDigits(initialCode));
  const [resendMessage, setResendMessage] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [resendError, setResendError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const inputRefs = useRef([]);
  const hasAutoSubmittedRef = useRef(false);

  useEffect(() => {
    let timer;
    if (timerRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1000);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(timer);
      setTimerRunning(false);
    }
    return () => {
      clearInterval(timer);
    };
  }, [timerRunning, timeLeft]);

  const handleDigitChange = (value, index) => {
    if (/^\d*$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);
      setVerifyError("");
      if (value && index < codeDigits.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const submitVerification = useCallback(
    async (rawCode) => {
      const code = normalizeVerificationCode(rawCode);
      if (code.length !== CODE_LENGTH) {
        setVerifyError(t("verifyCode.validation.verifyError"));
        return;
      }
      if (isVerifying) {
        return;
      }

      setIsVerifying(true);
      setVerifyError("");
      setResendMessage("");

      try {
        await verifyCode(email, code);
        clearDeepLinkVerificationCode("reset", email);
        setIsCodeVerified(true);
      } catch (error) {
        const errorMessage =
          error?.response?.error ||
          error?.response?.message ||
          t("verifyCode.validation.verifyError");
        setVerifyError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    },
    [email, isVerifying, t],
  );

  const handleVerifyCodeSubmit = async (e) => {
    e.preventDefault();
    await submitVerification(codeDigits.join(""));
  };

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (normalized.length !== CODE_LENGTH) {
      return;
    }
    setCodeDigits(normalized.split(""));
  }, [initialCode]);

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (
      hasAutoSubmittedRef.current ||
      normalized.length !== CODE_LENGTH ||
      !email
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    submitVerification(normalized);
  }, [initialCode, email, submitVerification]);

  const handleResendCode = async () => {
    try {
      setVerifyError("");
      setResendError("");
      setResendMessage("");
      setIsResending(true);
      const response = await resendCode(email);
      if (response.status === 200 || response.status === 201) {
        setResendMessage(response.data.message);
        setTimerRunning(true);
        setTimeLeft(300000);
      } else {
        setResendError(response.message);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.error ||
        error?.response?.message ||
        t("verifyCode.genericError");
      setResendError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? "0" : ""}${minutes}:${
      remainingSeconds < 10 ? "0" : ""
    }${remainingSeconds}`;
  };

  return (
    <>
      {isCodeVerified ? (
        <ResetForm email={email} setIsResetPass={setIsResetPass} />
      ) : (
        <>
          <h2 className="title-login">{t("verifyCode.title")}</h2>
          <p className="subtitle-login">{t("verifyCode.subtitle")}</p>
          <form onSubmit={handleVerifyCodeSubmit}>
            <div className="form-group-login">
              <div className="digits-container">
                {codeDigits.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleDigitChange(e.target.value, index)}
                    onFocus={(e) => e.target.select()}
                    ref={(el) => (inputRefs.current[index] = el)}
                    className="digit-input"
                    dir="ltr"
                  />
                ))}
              </div>
              {verifyError && (
                <p className="login-error">{verifyError}</p>
              )}
            </div>
            <div className="form-group-login">
              <button
                className="login-btn"
                type="submit"
                disabled={
                  isVerifying || codeDigits.some((digit) => digit === "")
                }
              >
                {t("verifyCode.buttons.submit")}
              </button>
            </div>
            {resendMessage && <p className="login-error">{resendMessage}</p>}
            {resendError && <p className="login-error">{resendError}</p>}
          </form>
          <div className="form-group-login">
            {timerRunning ? (
              <p className="text-white">
                {t("verifyCode.messages.timer", {
                  time: formatTime(timeLeft / 1000),
                })}
              </p>
            ) : (
              <button
                className="resend-btn"
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
              >
                {isResending
                  ? t("verifyCode.buttons.resending")
                  : t("verifyCode.buttons.resend")}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default VerifyCode;
